/**
 * SupportQueue.tsx — the SWAT/owner triage screen for open issue reports (FB-F06/F10) and non-Closed
 * support requests (FB-F15/F16, CH-F13), reskinned to the teal "upload" design system and mounted as
 * an in-place modal panel over AdminHome (SupportQueuePanel) rather than the old full-page navy screen.
 *
 * Issue reports can ALSO be resolved by the affected tool's own builder (FB-F10), so this screen
 * (unlike the review/moderation queues) can legitimately render for a caller who holds none of the
 * triage appointments but happens to be a builder — the backend enforces that per-issue, not this
 * page. A caller with neither a triage appointment nor any of their own tools' issues open just
 * sees an empty state, which is safe and honest.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a resolve/status change, calls reload() to refresh in place. The still-registered /support-queue
 * route renders through a thin SupportQueue() wrapper that opens the same panel for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  apiGet,
  apiPatch,
  apiPost,
  type OpenIssue,
  type OpenSupportRequest,
  type SupportRequestStatus,
} from './api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type SupportQueueLoaderData =
  | { state: 'ok'; issues: OpenIssue[]; requests: OpenSupportRequest[] }
  | { state: 'signedOut' }
  | { state: 'forbidden' };

export async function supportQueueLoader(): Promise<SupportQueueLoaderData> {
  const res = await apiGet<{ issues: OpenIssue[]; requests: OpenSupportRequest[] }>(
    '/api/support/queue',
  );
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/support/queue failed (${res.status})`);
  }
  return { state: 'ok', issues: res.body.issues, requests: res.body.requests };
}

/**
 * Calls the tool detail page's own issue-resolve route (`POST /api/tools/:toolId/issues/:issueId/
 * resolve`, routes/tools.ts) rather than a second implementation here — that route already applies
 * the FB-F10 rule (the tool's builder, or a live SWATMember/PlatformOwner appointment).
 */
function ResolveIssueForm({
  toolId,
  issueId,
  onResolved,
}: {
  toolId: string;
  issueId: string;
  onResolved: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (note.trim().length < 10) {
      setError('Say what was done to resolve it (at least 10 characters).');
      return;
    }
    setBusy(true);
    const res = await apiPost<{ ok: true; resolvedAt: string }>(
      `/api/tools/${toolId}/issues/${issueId}/resolve`,
      { resolutionNote: note.trim() },
    );
    setBusy(false);
    if (res.kind === 'ok') {
      onResolved();
      return;
    }
    if (res.kind === 'error' && (res.status === 403 || res.code === 'not_authorised')) {
      setError('You need to be on the SWAT team, the platform owner, or this tool’s builder.');
      return;
    }
    if (res.kind === 'error' && res.status === 409) {
      setError('This issue has already been resolved.');
      return;
    }
    setError('Could not resolve the issue. Please try again.');
  }

  return (
    <form onSubmit={submit}>
      <label className="kombi-field">
        <span>Resolution note</span>
        <textarea
          id={`resolve-note-${issueId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={2000}
        />
      </label>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div>
        <PrimaryAction submit>{busy ? 'Resolving…' : 'Mark solved'}</PrimaryAction>
      </div>
    </form>
  );
}

const STATUS_OPTIONS: SupportRequestStatus[] = ['Open', 'In progress', 'Resolved', 'Closed'];

function StatusChangeForm({
  requestId,
  currentStatus,
  onChanged,
}: {
  requestId: string;
  currentStatus: SupportRequestStatus;
  onChanged: () => void;
}) {
  const [status, setStatus] = useState<SupportRequestStatus>(currentStatus);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (reason.trim().length === 0) {
      setError('Every status change needs a reason — the person who raised it is told why.');
      return;
    }
    setBusy(true);
    const res = await apiPatch<void>(`/api/support/requests/${requestId}`, {
      status,
      reason: reason.trim(),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      onChanged();
      return;
    }
    if (res.kind === 'error' && res.status === 409) {
      setError('That is already the current status.');
      return;
    }
    // Triage is SWAT/owner-only — say so rather than the generic failure (matches the resolve form).
    if (res.kind === 'unauthorized' || (res.kind === 'error' && res.status === 403)) {
      setError(
        'You need to be on the SWAT team or the platform owner to change a request’s status.',
      );
      return;
    }
    setError('Could not change the status. Please try again.');
  }

  return (
    <form onSubmit={submit}>
      <div className="kombi-dialog-stack">
        <label className="kombi-field">
          <span>New status</span>
          <select
            id={`status-select-${requestId}`}
            value={status}
            onChange={(e) => setStatus(e.target.value as SupportRequestStatus)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="kombi-field">
          <span>Reason</span>
          <textarea
            id={`status-reason-${requestId}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
          />
        </label>
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div>
        <PrimaryAction submit>{busy ? 'Updating…' : 'Update status'}</PrimaryAction>
      </div>
    </form>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → supportQueueLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the two triage sub-sections on success — mutations reload() in place.
 */
export function SupportQueuePanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(supportQueueLoader);

  return (
    <KombiAdminOverlay title="Support & issues" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading the support queue…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the queue could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Support & issues" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>Open issue reports and non-closed support requests, triaged here.</Notice>

          <SectionHeader>Open issue reports</SectionHeader>
          {data.issues.length === 0 ? (
            <EmptyState>No open issue reports.</EmptyState>
          ) : (
            <ul className="kombi-dialog-stack">
              {data.issues.map((i) => (
                <li key={i.issueId} className="kombi-admin-card">
                  <div className="kombi-admin-value">{i.toolName}</div>
                  <div className="learn-note">
                    {i.kind} · reported by {i.reporterName ?? '[former colleague]'} ·{' '}
                    {shortWhen(i.createdAt)}
                  </div>
                  <div className="kombi-admin-value">{i.body}</div>
                  <ResolveIssueForm toolId={i.toolId} issueId={i.issueId} onResolved={reload} />
                </li>
              ))}
            </ul>
          )}

          <SectionHeader>Support requests</SectionHeader>
          {data.requests.length === 0 ? (
            <EmptyState>No open support requests.</EmptyState>
          ) : (
            <ul className="kombi-dialog-stack">
              {data.requests.map((r) => (
                <li key={r.requestId} className="kombi-admin-card">
                  <div className="kombi-admin-value">
                    {r.toolName ?? 'General request'} · {r.status}
                  </div>
                  <div className="learn-note">
                    raised by {r.raiserName ?? '[former colleague]'} · {shortWhen(r.createdAt)}
                  </div>
                  <div className="kombi-admin-value">{r.body}</div>
                  <StatusChangeForm
                    requestId={r.requestId}
                    currentStatus={r.status}
                    onChanged={reload}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /support-queue deep link. Opens the panel always-open
 * over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function SupportQueue() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <SupportQueuePanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
